import React, { useMemo } from 'react';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { buildBatchAfasPreview } from '../utils/afasDagdeelAfasPreview';

const prettyJson = (value) => {
    if (value == null) {
        return '—';
    }

    try {
        return JSON.stringify(value, null, 2);
    } catch {
        return String(value);
    }
};

/**
 * Debug modal: AFAS POST body (job) + local ClooserMeta (not POSTed).
 */
const AfasBatchPayloadModal = ({ isOpen, onClose, row }) => {
    const preview = useMemo(() => buildBatchAfasPreview(row), [row]);
    const isLangdurig = preview.mode === 'langdurig';
    const hasDagdelen = preview.dagdelenCount > 0;

    const title = row
        ? `Debug — batch ${row.id}${row.verzoek ? ` / verzoek ${row.verzoek}` : ''}`
        : 'Debug';

    return (
        <Modal
            isOpen={isOpen}
            toggle={onClose}
            centered
            size="lg"
        >
            <ModalHeader toggle={onClose}>
                {title}
            </ModalHeader>
            <ModalBody>
                <p className="text-muted small mb-3">
                    Alleen ter controle. Dit scherm stuurt niets naar AFAS.
                </p>

                {isLangdurig ? (
                    <>
                        <h6 className="fw-semibold mb-2">Langdurig rooster (periode)</h6>
                        <p className="text-muted small mb-2">
                            Bron: <code>ClooserProlonged</code> (lokaal).{' '}
                            <code>DaBe</code>/<code>DaEn</code> = begin/eind; rooster{' '}
                            <code>Wd*</code>/<code>Ft*</code> uit weekdagen-WTF over de hele
                            periode.
                        </p>
                        <pre className="afas-batch-payload-modal__json">
                            {prettyJson(preview.period)}
                        </pre>
                        <h6 className="fw-semibold mb-2 mt-4">Geaggregeerd rooster (langdurig)</h6>
                        <pre className="afas-batch-payload-modal__json">
                            {prettyJson(preview.aggregatedTimeTable)}
                        </pre>
                    </>
                ) : (
                    <>
                        <h6 className="fw-semibold mb-2">Dagdelen (selectie uit Openstaand)</h6>
                        <p className="text-muted small mb-2">
                            Bron: <code>ClooserMeta.dagdelen</code>. Per dagdeel worden in AFAS
                            o.a. <code>DaBe</code>/<code>DaEn</code>, <code>DvbSubs</code> en
                            rooster <code>Wd*</code>/<code>Ft*</code> gezet (legacy bluebox).
                        </p>
                        {hasDagdelen ? (
                            <>
                                <pre className="afas-batch-payload-modal__json">
                                    {prettyJson(preview.perDagdeel)}
                                </pre>
                                <h6 className="fw-semibold mb-2 mt-4">
                                    Geaggregeerd rooster (weekbatch)
                                </h6>
                                <pre className="afas-batch-payload-modal__json">
                                    {prettyJson(preview.aggregatedTimeTable)}
                                </pre>
                            </>
                        ) : (
                            <p className="text-muted small mb-0">
                                Geen dagdelen in deze batch. Alleen batches via Verwerken
                                bevatten dagdeel-data; testrijen hebben vaak alleen
                                header-velden.
                            </p>
                        )}
                    </>
                )}

                <h6 className="fw-semibold mb-2 mt-4">Te versturen naar AFAS (job)</h6>
                <p className="text-muted small mb-2">
                    POST-body voor <code>KnEmployee/AfasContract</code> — alleen{' '}
                    <code>AfasEmployee</code> (volledige Fields-schema, nulls behouden).{' '}
                    <code>ClooserMeta</code> / <code>ClooserProlonged</code> gaan niet mee.
                </p>
                <pre className="afas-batch-payload-modal__json">
                    {prettyJson(row?.afasPostBody)}
                </pre>

                <h6 className="fw-semibold mb-2 mt-4">ClooserMeta (lokaal)</h6>
                <p className="text-muted small mb-2">
                    Volledige snapshot in <code>afas_batches</code> (verzoek, extra, dagdelen).
                </p>
                <pre className="afas-batch-payload-modal__json">
                    {prettyJson(row?.clooserMeta)}
                </pre>

                {row?.clooserProlonged ? (
                    <>
                        <h6 className="fw-semibold mb-2 mt-4">ClooserProlonged (lokaal)</h6>
                        <pre className="afas-batch-payload-modal__json">
                            {prettyJson(row.clooserProlonged)}
                        </pre>
                    </>
                ) : null}
            </ModalBody>
            <ModalFooter>
                <Button type="button" color="link" onClick={onClose} className="btn-color-style">
                    Scherm sluiten
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default AfasBatchPayloadModal;
